/**
 * FEAT-015 — workspace-level vehicle library page (?page=vehicles).
 *
 * Lists every vehicle profile, lets the user add/edit/duplicate/delete and
 * import/export the library as JSON. The five seeded presets surface here
 * exactly like user-created profiles (badge marks the preset origin); they
 * can be edited or deleted without re-seeding.
 *
 * Profiles are NOT bound to a station here — connector binding lives on
 * StationPage (FEAT-011). This page is purely the library editor.
 */
import { useEffect, useRef, useState } from 'react';
import type { ChargingCurvePoint, VehicleProfileSnapshot } from '@/state/types';
import {
  useVehicleStore,
  type NewVehicleInput,
  type MutationResult,
} from '@/state/useVehicleStore';
import { seedVehiclesIfEmpty } from '@/state/repos';
import { Logo } from '@/ui/components/Logo';
import { ToastViewport } from '@/ui/components/Toast';
import { CurveEditor } from '@/ui/components/CurveEditor';
import type { CurvePoint } from '@/lib/curveMath';
import { useConfirm } from '@/hooks/useConfirm';
import { toast, toastError } from '@/state/useToastStore';

type FormMode =
  | { kind: 'add' }
  | { kind: 'edit'; source: VehicleProfileSnapshot }
  | null;

/** One editable charging-curve row (strings while editing). */
interface CurveRow {
  socPct: string;
  powerKw: string;
}

interface FormState {
  name: string;
  batteryKwh: string;
  maxAcPowerKw: string;
  maxDcPowerKw: string;
  defaultStartSocPct: string;
  /** DC charging curve rows; empty = flat maxDcPowerKw. */
  chargingCurve: CurveRow[];
}

const EMPTY_FORM: FormState = {
  name: '',
  batteryKwh: '',
  maxAcPowerKw: '',
  maxDcPowerKw: '',
  defaultStartSocPct: '',
  chargingCurve: [],
};

function fromSnap(s: VehicleProfileSnapshot): FormState {
  return {
    name: s.name,
    batteryKwh: String(s.batteryKwh),
    maxAcPowerKw: String(s.maxAcPowerKw),
    maxDcPowerKw: String(s.maxDcPowerKw),
    defaultStartSocPct: String(s.defaultStartSocPct),
    chargingCurve: (s.chargingCurve ?? []).map((p) => ({
      socPct: String(p.socPct),
      powerKw: String(p.powerKw),
    })),
  };
}

function parseInput(f: FormState): NewVehicleInput | { error: string } {
  const batteryKwh = Number(f.batteryKwh);
  const maxAcPowerKw = Number(f.maxAcPowerKw);
  const maxDcPowerKw = Number(f.maxDcPowerKw);
  const defaultStartSocPct = Number(f.defaultStartSocPct);
  if (
    !Number.isFinite(batteryKwh) ||
    !Number.isFinite(maxAcPowerKw) ||
    !Number.isFinite(maxDcPowerKw) ||
    !Number.isFinite(defaultStartSocPct)
  ) {
    return { error: 'All numeric fields are required.' };
  }
  // Parse the curve: blank rows are dropped; an empty result clears the curve
  // (the store treats [] as "flat maxDcPowerKw").
  const chargingCurve: ChargingCurvePoint[] = [];
  for (const row of f.chargingCurve) {
    const socRaw = row.socPct.trim();
    const pwrRaw = row.powerKw.trim();
    if (socRaw === '' && pwrRaw === '') continue;
    const socPct = Number(socRaw);
    const powerKw = Number(pwrRaw);
    if (!Number.isFinite(socPct) || socPct < 0 || socPct > 100) {
      return { error: 'Charging-curve SoC must be a number in [0, 100].' };
    }
    if (!Number.isFinite(powerKw) || powerKw < 0) {
      return { error: 'Charging-curve power must be a number ≥ 0 kW.' };
    }
    chargingCurve.push({ socPct, powerKw });
  }
  return {
    name: f.name,
    batteryKwh,
    maxAcPowerKw,
    maxDcPowerKw,
    defaultStartSocPct,
    chargingCurve,
  };
}

/**
 * Bridge between the numeric-table form rows (strings, possibly blank while
 * typing) and the SVG editor (numeric points). The two views share one source
 * of truth — `form.chargingCurve` — so a drag and a typed edit stay in sync.
 */
function curveRowsToPoints(rows: readonly CurveRow[]): CurvePoint[] {
  const out: CurvePoint[] = [];
  for (const r of rows) {
    const socPct = Number(r.socPct);
    const powerKw = Number(r.powerKw);
    if (r.socPct.trim() === '' || r.powerKw.trim() === '') continue;
    if (!Number.isFinite(socPct) || !Number.isFinite(powerKw)) continue;
    out.push({ socPct, powerKw });
  }
  return out;
}

function pointsToCurveRows(points: readonly CurvePoint[]): CurveRow[] {
  return points.map((p) => ({ socPct: String(p.socPct), powerKw: String(p.powerKw) }));
}

export function VehiclesPage(): JSX.Element {
  const profiles = useVehicleStore((s) => s.profiles);
  const status = useVehicleStore((s) => s.status);
  const loadAll = useVehicleStore((s) => s.loadAll);
  const createProfile = useVehicleStore((s) => s.create);
  const updateProfile = useVehicleStore((s) => s.update);
  const duplicateProfile = useVehicleStore((s) => s.duplicate);
  const removeProfile = useVehicleStore((s) => s.remove);
  const importJson = useVehicleStore((s) => s.importJson);
  const exportJson = useVehicleStore((s) => s.exportJson);
  const confirm = useConfirm();

  const [mode, setMode] = useState<FormMode>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    void (async (): Promise<void> => {
      // BUG-010 — App.tsx boot effect normally seeds + loads vehicles
      // before VehiclesPage mounts. This block is now defence-in-depth:
      // it runs only if the store is still empty (e.g. boot effect
      // raced or failed). The toast also gates on `inserted > 0`, so
      // a normal boot (store already hydrated) is silent.
      const inserted = await seedVehiclesIfEmpty();
      await loadAll();
      if (inserted > 0) toast(`Loaded ${inserted} starter vehicles`);
    })();
  }, [loadAll]);

  const closeForm = (): void => {
    setMode(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const openAdd = (): void => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setMode({ kind: 'add' });
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const openEdit = (source: VehicleProfileSnapshot): void => {
    setForm(fromSnap(source));
    setFormError(null);
    setMode({ kind: 'edit', source });
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handleSave = async (): Promise<void> => {
    setFormError(null);
    const parsed = parseInput(form);
    if ('error' in parsed) {
      setFormError(parsed.error);
      return;
    }
    let result: MutationResult;
    if (mode?.kind === 'edit') {
      result = await updateProfile(mode.source.id, parsed);
    } else {
      result = await createProfile(parsed);
    }
    if (!result.ok) {
      setFormError(result.error ?? 'Save failed.');
      return;
    }
    toast(mode?.kind === 'edit' ? `Updated ${parsed.name}` : `Added ${parsed.name}`);
    closeForm();
  };

  const handleExport = (): void => {
    const blob = new Blob([exportJson()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vehicles-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast(`Exported ${profiles.length} vehicles`);
  };

  const handleFileChosen = async (file: File): Promise<void> => {
    const text = await file.text();
    const result = await importJson(text);
    if (!result.ok) {
      toastError(result.error ?? 'Import failed.');
      return;
    }
    if (result.imported === 0 && result.skipped > 0) {
      toastError(`No new vehicles imported — ${result.skipped} skipped (duplicates / invalid)`);
      return;
    }
    toast(
      result.skipped > 0
        ? `Imported ${result.imported} vehicles (${result.skipped} skipped)`
        : `Imported ${result.imported} vehicles`,
    );
  };

  const formHeader = mode?.kind === 'edit' ? `Edit ${mode.source.name}` : 'Add vehicle';
  const saveLabel = mode?.kind === 'edit' ? 'Save' : 'Add';

  return (
    <main
      className="min-h-full bg-slate-950 p-8 text-slate-200"
      data-testid="vehicles-page"
    >
      <header className="mx-auto mb-6 flex max-w-3xl flex-col gap-3 md:flex-row md:items-baseline md:justify-between">
        <div className="flex items-center gap-3">
          <Logo variant="mark" size="lg" testId="vehicles" href="/welcome" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl">
              VirtualCharger
            </h1>
            <p className="text-sm text-slate-400">Vehicle profiles</p>
          </div>
        </div>
        <div className="flex flex-wrap items-baseline gap-3">
          <a
            href="?"
            className="text-xs text-slate-400 underline-offset-2 hover:text-slate-100 hover:underline"
            data-testid="vehicles-back-to-stations"
          >
            ← Stations
          </a>
          <span className="text-xs text-slate-500" data-testid="vehicles-count">
            {profiles.length} saved
          </span>
          <button
            type="button"
            onClick={() => (mode === null ? openAdd() : closeForm())}
            className="min-h-[44px] rounded bg-primary-hover px-4 py-2 text-sm font-medium text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid="vehicles-add-toggle"
          >
            {mode === null ? 'Add vehicle' : 'Close'}
          </button>
        </div>
      </header>

      <section
        className="mx-auto mb-4 max-w-3xl flex items-center justify-end gap-2"
        data-testid="vehicles-toolbar"
      >
        <button
          type="button"
          onClick={handleExport}
          disabled={profiles.length === 0}
          className="rounded bg-slate-700 px-3 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="vehicles-export"
        >
          Export JSON
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="rounded bg-slate-700 px-3 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
          data-testid="vehicles-import"
        >
          Import JSON
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              void handleFileChosen(file);
            }
            e.target.value = '';
          }}
          data-testid="vehicles-import-input"
        />
      </section>

      {mode !== null ? (
        <section
          ref={formRef}
          className="mx-auto mb-4 max-w-3xl rounded border border-slate-700 bg-slate-900 p-4"
          data-testid="vehicles-form"
          data-mode={mode.kind}
        >
          <h2 className="mb-3 text-sm font-medium text-slate-100">{formHeader}</h2>
          <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-slate-400">Name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Hyundai Ioniq 5"
                className="rounded border border-slate-600 bg-slate-950 px-2 py-1 text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="vehicles-form-name"
                aria-label="name"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-slate-400">Battery (kWh)</span>
              <input
                type="number"
                inputMode="decimal"
                value={form.batteryKwh}
                onChange={(e) => setForm({ ...form, batteryKwh: e.target.value })}
                placeholder="e.g. 77"
                min="0"
                step="any"
                className="rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="vehicles-form-battery"
                aria-label="battery kWh"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-slate-400">Max AC (kW)</span>
              <input
                type="number"
                inputMode="decimal"
                value={form.maxAcPowerKw}
                onChange={(e) => setForm({ ...form, maxAcPowerKw: e.target.value })}
                placeholder="e.g. 11"
                min="0"
                step="any"
                className="rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="vehicles-form-ac"
                aria-label="max AC kW"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-slate-400">Max DC (kW)</span>
              <input
                type="number"
                inputMode="decimal"
                value={form.maxDcPowerKw}
                onChange={(e) => setForm({ ...form, maxDcPowerKw: e.target.value })}
                placeholder="e.g. 220"
                min="0"
                step="any"
                className="rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="vehicles-form-dc"
                aria-label="max DC kW"
              />
            </label>
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-slate-400">Default starting SoC (%)</span>
              <input
                type="number"
                inputMode="decimal"
                value={form.defaultStartSocPct}
                onChange={(e) =>
                  setForm({ ...form, defaultStartSocPct: e.target.value })
                }
                placeholder="e.g. 20"
                min="0"
                max="100"
                step="any"
                className="rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                data-testid="vehicles-form-soc"
                aria-label="default start SoC %"
              />
            </label>
          </div>
          <fieldset
            className="mt-3 rounded border border-slate-700 p-3"
            data-testid="vehicles-form-curve"
          >
            <legend className="px-1 text-xs text-slate-400">DC charging curve (optional)</legend>
            <p className="mb-2 text-[11px] text-slate-500">
              Max DC power accepted at each SoC. Power is linearly interpolated between points
              and tapers the charge during DC sessions. Leave empty for a flat max-DC cap.
            </p>
            <CurveEditor
              points={curveRowsToPoints(form.chargingCurve)}
              maxDcKw={Number(form.maxDcPowerKw) || 0}
              onChange={(pts) => setForm({ ...form, chargingCurve: pointsToCurveRows(pts) })}
              testId="vehicles-form-curve-editor"
            />
            <p className="mb-1 mt-3 text-[11px] text-slate-500">
              Or edit exact values in the table below (kept in sync with the graph):
            </p>
            <div className="space-y-1">
              {form.chargingCurve.map((row, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="100"
                    step="any"
                    value={row.socPct}
                    onChange={(e) => {
                      const next = [...form.chargingCurve];
                      next[i] = { ...row, socPct: e.target.value };
                      setForm({ ...form, chargingCurve: next });
                    }}
                    placeholder="SoC %"
                    className="w-20 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                    data-testid={`vehicles-form-curve-soc-${i}`}
                    aria-label={`curve point ${i + 1} SoC %`}
                  />
                  <span className="text-slate-500">% →</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    value={row.powerKw}
                    onChange={(e) => {
                      const next = [...form.chargingCurve];
                      next[i] = { ...row, powerKw: e.target.value };
                      setForm({ ...form, chargingCurve: next });
                    }}
                    placeholder="kW"
                    className="w-20 rounded border border-slate-600 bg-slate-950 px-2 py-1 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
                    data-testid={`vehicles-form-curve-kw-${i}`}
                    aria-label={`curve point ${i + 1} power kW`}
                  />
                  <span className="text-slate-500">kW</span>
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        chargingCurve: form.chargingCurve.filter((_, j) => j !== i),
                      })
                    }
                    className="rounded bg-slate-700 px-2 py-1 text-slate-200 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    data-testid={`vehicles-form-curve-remove-${i}`}
                    aria-label={`remove curve point ${i + 1}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  chargingCurve: [...form.chargingCurve, { socPct: '', powerKw: '' }],
                })
              }
              className="mt-2 rounded bg-slate-700 px-2 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="vehicles-form-curve-add"
            >
              + Add curve point
            </button>
          </fieldset>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                void handleSave();
              }}
              className="rounded bg-primary-hover px-3 py-1 text-xs text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base"
              data-testid="vehicles-form-save"
            >
              {saveLabel}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="rounded bg-slate-700 px-3 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="vehicles-form-cancel"
            >
              Cancel
            </button>
            {formError !== null ? (
              <p className="ml-2 text-xs text-rose-300" data-testid="vehicles-form-error">
                {formError}
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      {status === 'ready' && profiles.length === 0 ? (
        <section
          className="mx-auto max-w-3xl rounded border border-slate-700 bg-slate-900 p-6"
          data-testid="vehicles-empty"
        >
          <p className="text-sm text-slate-300">No vehicle profiles yet.</p>
          <p className="mt-2 text-xs text-slate-500">
            The five seeded presets failed to load. Click{' '}
            <span className="font-medium">Add vehicle</span> to create one manually.
          </p>
        </section>
      ) : null}

      <ul className="mx-auto max-w-3xl space-y-2">
        {profiles.map((p) => (
          <li
            key={p.id}
            className="flex flex-col gap-3 rounded border border-slate-700 bg-slate-900 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`vehicles-row-${p.id}`}
          >
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="truncate text-sm font-medium text-slate-100">
                  {p.name}
                </span>
                {p.isPreset ? (
                  <span
                    className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-300"
                    data-testid={`vehicles-preset-${p.id}`}
                  >
                    Preset
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-slate-500">
                {p.batteryKwh} kWh · AC {p.maxAcPowerKw} kW · DC {p.maxDcPowerKw} kW · start{' '}
                {p.defaultStartSocPct}%
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => openEdit(p)}
                className="rounded bg-slate-700 px-3 py-1 text-xs text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid={`vehicles-edit-${p.id}`}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  void duplicateProfile(p.id).then((r) => {
                    if (r.ok) toast(`Duplicated ${p.name}`);
                    else toastError(r.error ?? 'Duplicate failed.');
                  });
                }}
                className="rounded bg-sky-700 px-3 py-1 text-xs text-sky-50 hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-300"
                data-testid={`vehicles-duplicate-${p.id}`}
              >
                Duplicate
              </button>
              <button
                type="button"
                onClick={() => {
                  confirm(`delete vehicle profile ${p.name}`, () => {
                    void removeProfile(p.id).then(() => toast(`Deleted ${p.name}`));
                  });
                }}
                className="rounded bg-rose-700 px-3 py-1 text-xs text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
                data-testid={`vehicles-delete-${p.id}`}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
      <ToastViewport />
    </main>
  );
}
